import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bike, Package, MapPin, Users, X, ChevronLeft, Check, BadgeDollarSign, Route
} from 'lucide-react';
import { SERVICE_TYPES } from '../../constants';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const TypeBadge = ({ type }) => (
  <span className="inline-flex items-center gap-1 rounded-md bg-[rgba(14,171,176,0.14)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--bry-accent)]">
    {type === SERVICE_TYPES.DELIVERY ? <Package size={12} /> : <Bike size={12} />}
    {type === SERVICE_TYPES.DELIVERY ? 'Entrega' : 'Viaje'}
  </span>
);

const RouteBlock = ({ service }) => (
  <div className="rounded-xl border border-gray-800 bg-gray-900/60 px-3 py-2">
    <div className="flex items-center gap-2 py-0.5">
      <MapPin size={14} className="shrink-0 text-[var(--bry-accent)]" />
      <span className="truncate text-xs font-semibold text-gray-100">
        {service.pickup?.address || 'Ubicación GPS'}
      </span>
    </div>
    <div className="ml-[6px] h-2.5 w-px bg-gray-600" />
    <div className="flex items-center gap-2 py-0.5">
      <MapPin size={14} className="shrink-0 text-gray-400" />
      <span className="truncate text-xs font-semibold text-gray-300">
        {service.dropoff?.address || 'Destino en Mapa'}
      </span>
    </div>
    {service.reference_points?.length > 0 && (
      <span className="mt-1 block text-[10px] font-semibold text-gray-500">
        +{service.reference_points.length} referencia(s)
      </span>
    )}
  </div>
);

const StepDetail = ({ service, fare }) => {
  const { tarifa, commissionPct, priceTotal, netTotal, distKm } = fare;

  return (
    <div className="space-y-2 pt-1">
      <div className="flex items-center gap-1.5">
        <TypeBadge type={service.type} />
        {service.offered_price && (
          <span className="inline-flex items-center rounded-md bg-yellow-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-yellow-400">
            Oferta
          </span>
        )}
        {service.passenger_count > 1 && (
          <span className="inline-flex items-center gap-1 rounded-md bg-gray-800 px-2 py-0.5 text-[10px] font-bold text-gray-300">
            <Users size={11} /> {service.passenger_count}
          </span>
        )}
        <span className="ml-auto whitespace-nowrap text-xs font-bold text-gray-300">{distKm} km</span>
      </div>

      <RouteBlock service={service} />

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-2.5">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">Total pasajero</span>
          <span className="mt-1 block text-lg font-black leading-none text-white">
            {service.offered_price && (
              <span className="mr-1.5 text-xs font-semibold text-gray-500 line-through">{money(service.price)}</span>
            )}
            {money(priceTotal)}
          </span>
        </div>
        <div className="rounded-xl border border-[rgba(14,171,176,0.45)] bg-[rgba(14,171,176,0.12)] p-2.5">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-[var(--bry-accent)]">Recibes</span>
          <span data-testid="fare-net" className="mt-1 block text-lg font-black leading-none text-[var(--bry-accent)] tabular-nums">
            {money(netTotal)}
          </span>
        </div>
      </div>

      <div data-testid="fare-transparency" className="rounded-xl border border-gray-800/70 bg-gray-800/30 px-3 py-2">
        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-gray-400">
          <span className="inline-flex items-center gap-1"><Route size={11} /> Desglose</span>
          <span data-testid="fare-commission" className="text-[var(--bry-accent)]">{commissionPct}% comision</span>
        </div>
        <div className="mt-1 grid grid-cols-3 gap-1 text-[11px] text-gray-300 tabular-nums">
          <span data-testid="fare-base">Base {money(tarifa.breakdown.base)}</span>
          <span data-testid="fare-dist">Dist. {money(tarifa.breakdown.distancia)}</span>
          <span data-testid="fare-time">Tiempo {money(tarifa.breakdown.tiempo)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between border-t border-gray-800 pt-1 text-[11px]">
          <span className="text-gray-400">Total pasajero</span>
          <span data-testid="fare-total" className="font-bold text-gray-200 tabular-nums">{money(priceTotal)}</span>
        </div>
      </div>
    </div>
  );
};

const StepDecision = ({ service, fare, mode, setMode, offerValue, setOfferValue, minOffer, maxOffer, offerValid }) => {
  const { priceTotal, netTotal } = fare;

  return (
    <div className="space-y-2 pt-1">
      <p className="text-xs text-gray-300 m-0">Elige cómo quieres responder a esta solicitud.</p>

      <button
        type="button"
        onClick={() => setMode('accept')}
        className={`w-full rounded-xl border p-3 text-left transition-colors ${
          mode === 'accept'
            ? 'border-[var(--bry-accent)] bg-[rgba(14,171,176,0.14)]'
            : 'border-gray-800 bg-gray-900/60 hover:border-gray-700'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-white">Aceptar tarifa</span>
          <span className="text-base font-black text-white tabular-nums">{money(priceTotal)}</span>
        </div>
        <span className="mt-0.5 block text-[11px] text-gray-300">
          Recibes {money(netTotal)} por este servicio.
        </span>
      </button>

      <button
        type="button"
        onClick={() => setMode('offer')}
        className={`w-full rounded-xl border p-3 text-left transition-colors ${
          mode === 'offer'
            ? 'border-yellow-500/60 bg-yellow-500/10'
            : 'border-gray-800 bg-gray-900/60 hover:border-gray-700'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-white">
            <BadgeDollarSign size={15} className="text-yellow-400" /> Hacer contraoferta
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-yellow-400">1 sola vez</span>
        </div>
        <span className="mt-0.5 block text-[11px] text-gray-300">
          Propón un precio distinto al del pasajero.
        </span>
      </button>

      {mode === 'offer' && (
        <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3">
          <label htmlFor="counter-offer" className="text-[10px] font-bold uppercase tracking-wider text-gray-300">
            Tu contraoferta (USD)
          </label>
          <input
            id="counter-offer"
            type="number"
            inputMode="decimal"
            min={minOffer}
            max={maxOffer}
            step="0.50"
            value={offerValue}
            onChange={(e) => setOfferValue(e.target.value)}
            placeholder={String(service.price || '')}
            className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-lg font-bold text-white outline-none focus:border-yellow-500/60"
          />
          <div className="mt-1 flex items-center justify-between text-[10px]">
            <span className="text-gray-400">Rango {money(minOffer)} – {money(maxOffer)}</span>
            {offerValue !== '' && !offerValid && <span className="font-bold text-red-400">Fuera de rango</span>}
          </div>
          <p className="mt-1.5 mb-0 text-[10px] leading-snug text-yellow-300">
            Solo puedes hacer una contraoferta. Si el pasajero la rechaza, el servicio se cancela.
          </p>
        </div>
      )}
    </div>
  );
};

const StepConfirm = ({ service, fare, mode, offerValue }) => {
  const { priceTotal, netTotal } = fare;
  const isOffer = mode === 'offer';

  return (
    <div className="space-y-2 pt-1">
      <RouteBlock service={service} />

      <div className="flex items-center gap-1.5">
        <TypeBadge type={service.type} />
        {service.passenger_count > 1 && (
          <span className="inline-flex items-center gap-1 rounded-md bg-gray-800 px-2 py-0.5 text-[10px] font-bold text-gray-300">
            <Users size={11} /> {service.passenger_count}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-2.5">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400">
            {isOffer ? 'Pasajero paga' : 'Cobras al pasajero'}
          </span>
          <span className="mt-1 block text-lg font-black leading-none text-white tabular-nums">{money(priceTotal)}</span>
        </div>
        <div className="rounded-xl border border-[rgba(14,171,176,0.45)] bg-[rgba(14,171,176,0.12)] p-2.5">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-[var(--bry-accent)]">
            {isOffer ? 'Tu oferta' : 'Recibes'}
          </span>
          <span className="mt-1 block text-lg font-black leading-none text-[var(--bry-accent)] tabular-nums">
            {isOffer ? money(offerValue) : money(netTotal)}
          </span>
        </div>
      </div>

      {isOffer ? (
        <p className="m-0 rounded-xl border border-yellow-500/25 bg-yellow-500/10 p-2.5 text-[11px] leading-snug text-yellow-300">
          Enviaremos tu contraoferta al pasajero. Si la rechaza, la solicitud se cancela automáticamente.
        </p>
      ) : (
        <p className="m-0 text-[11px] leading-snug text-gray-300">
          {service.type === SERVICE_TYPES.DELIVERY
            ? 'Al aceptar, dirígete al punto de recogida para retirar el paquete.'
            : 'Al aceptar, dirígete al punto de recogida del pasajero.'}
        </p>
      )}
    </div>
  );
};

const STEPS = [
  { id: 'detail', label: 'Detalle', Component: StepDetail },
  { id: 'decision', label: 'Decisión', Component: StepDecision },
  { id: 'confirm', label: 'Confirmar', Component: StepConfirm },
];

const ServiceOfferWizard = ({ service, fare, onClose, onAccept, onReject, onCounterOffer }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [mode, setMode] = useState('accept');
  const [offerValue, setOfferValue] = useState('');

  if (!service) return null;

  const current = STEPS[stepIndex];
  const StepComponent = current.Component;
  const basePrice = Number(service.price) || 0;
  const minOffer = Number((basePrice * 0.5).toFixed(2));
  const maxOffer = Number((basePrice * 2).toFixed(2));
  const offerNumber = parseFloat(offerValue);
  const offerValid = Number.isFinite(offerNumber) && offerNumber >= minOffer && offerNumber <= maxOffer;
  const canContinue = current.id === 'decision' ? (mode === 'accept' || offerValid) : true;

  const handleBack = () => setStepIndex((prev) => Math.max(0, prev - 1));

  const handleNext = () => {
    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
      return;
    }
    if (mode === 'offer') {
      onCounterOffer(service, offerNumber);
    } else {
      onAccept(service);
    }
  };

  const handleReject = () => onReject(service.id || service.service_id);

  const primaryLabel = (() => {
    if (stepIndex < STEPS.length - 1) return 'Continuar';
    if (mode === 'offer') return `Enviar oferta ${money(offerNumber)}`;
    return `Aceptar viaje ${money(fare.netTotal)}`;
  })();

  const modalNode = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Solicitud de servicio"
      className="fixed inset-0 z-[10060] flex items-center justify-center p-3"
      style={{ background: 'rgba(2, 6, 23, 0.78)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="flex w-full flex-col overflow-hidden shadow-2xl"
        style={{
          maxWidth: 440,
          maxHeight: 'calc(100dvh - 24px)',
          background: '#0F172A',
          borderRadius: 20,
          border: '1px solid rgba(148, 163, 184, 0.22)',
        }}
      >
        {/* Header */}
        <div className="shrink-0 px-4 pt-3 pb-2 border-b border-gray-800/80">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                Paso {stepIndex + 1} de {STEPS.length} · {current.label}
              </span>
              <h2 className="m-0 text-base font-black text-white">Solicitud de servicio</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-700 bg-gray-800 text-gray-300 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mt-2 flex gap-1.5" aria-hidden="true">
            {STEPS.map((step, index) => (
              <span
                key={step.id}
                className="h-1.5 flex-1 rounded-full transition-colors"
                style={{ backgroundColor: index <= stepIndex ? '#0EABB0' : 'rgba(148, 163, 184, 0.22)' }}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2.5">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={current.id}
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -14 }}
              transition={{ duration: 0.14 }}
            >
              <StepComponent
                service={service}
                fare={fare}
                mode={mode}
                setMode={setMode}
                offerValue={offerValue}
                setOfferValue={setOfferValue}
                minOffer={minOffer}
                maxOffer={maxOffer}
                offerValid={offerValid}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Sticky Footer — Always Visible */}
        <div
          className="flex shrink-0 items-center gap-2.5 border-t px-4 py-3"
          style={{
            background: '#0B1120',
            borderColor: 'rgba(148, 163, 184, 0.18)',
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {stepIndex > 0 ? (
            <button
              type="button"
              onClick={handleBack}
              style={{ minHeight: 46 }}
              className="flex items-center justify-center gap-1 rounded-xl border border-gray-700 bg-gray-800 px-3.5 text-xs font-bold text-gray-200 hover:bg-gray-700"
            >
              <ChevronLeft size={16} /> Atrás
            </button>
          ) : (
            <button
              type="button"
              onClick={handleReject}
              style={{
                minHeight: 46,
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                borderColor: 'rgba(239, 68, 68, 0.45)',
                color: '#FCA5A5',
              }}
              className="flex items-center justify-center rounded-xl border px-4 text-xs font-extrabold hover:bg-red-500/25"
            >
              Rechazar
            </button>
          )}

          <button
            type="button"
            onClick={handleNext}
            disabled={!canContinue}
            style={{
              minHeight: 46,
              backgroundColor: mode === 'offer' && current.id === 'confirm' ? '#D97706' : '#0EABB0',
              color: '#FFFFFF',
            }}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl px-4 text-sm font-black text-white shadow-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            {current.id === 'confirm' && mode === 'accept' && <Check size={18} strokeWidth={3} />}
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalNode, document.body);
  }
  return modalNode;
};

export default ServiceOfferWizard;
